const _excluded = ["id"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { concat } from 'lodash-es';
import { useOverlayContext, TextButton, Typography, useStateInRef } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { AddServicesButton } from "../../AddServicesButton/AddServicesButton";
import EditServiceContent from "../../EditServiceContentModal/EditServiceContentModal";
import ServiceBuilder from "../../ServiceCreation/ServiceBuilder/ServiceBuilder";
import { ServicesListStructure } from "../../ServicesListStructure/ServicesListStructure";
import { useEventReporter } from "../../../event-reporter";
import { scrollIntoView } from "../../../infra/utils";
import { serviceModel } from "../../../models";
import { validateBillingDayOfMonth } from "../../../models/service";
import { useAppConfig } from "../../../store/store-context";
import DraftServiceInstance from "../DraftServiceInstance/DraftServiceInstance";
import moveItem from "./moveItem";
import styles from "./ServicePackages.module.scss";
import ServicePackagesTab from "./ServicePackagesTab";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const tabPanelId = 'services-in-pkg';
const autoprefix = 'formling-statement-of-work-';
const addServicesTestid = autoprefix + 'btn-add-service';
function getFreeNewBundleName(existingNames) {
  let i = 0;
  let name = '';
  do {
    i++;
    name = "Option ".concat(i);
  } while (existingNames.includes(name));
  return name;
}
const ServicePackages = /*#__PURE__*/forwardRef(function ServicePackages(props, ref) {
  var _bundles$;
  const {
    services,
    bundles,
    agreementData,
    showEmptyWarning,
    showInvalidPackagesWarning,
    showServiceValidationWarning,
    showErrors,
    scrollToServiceId,
    isReadOnly,
    advancedSettingsProps,
    isServiceInvalid,
    setServices,
    setBundles,
    onHasError
  } = props;
  const {
    reportButtonClick
  } = useEventReporter();
  const [selectedPackage, setSelectedPackage] = useState((_bundles$ = bundles[0]) === null || _bundles$ === void 0 ? void 0 : _bundles$.id);
  const getStateInRef = useStateInRef(props);
  const servicesRef = useRef({});
  const scrollToServiceCounter = useRef(1);
  const overlayContext = useOverlayContext();
  const {
    disableScrollAnimation
  } = useAppConfig();
  const isInPackage = bundles.length > 1;
  useEffect(() => {
    const hasError = services.reduce((anyError, service) => {
      const billingDayOfMonth = serviceModel.billingDayOfMonth(service);
      const isBilledUpfront = serviceModel.isBilledUpfront(service);
      const serviceHasError = billingDayOfMonth != null && isBilledUpfront != null ? validateBillingDayOfMonth(billingDayOfMonth, isBilledUpfront) : false;
      return anyError || serviceHasError;
    }, false);
    onHasError && onHasError(hasError);
  }, [services, onHasError]);
  useEffect(() => {
    if (scrollToServiceId) {
      const {
        bundles,
        services
      } = getStateInRef();
      if (bundles.length > 0) {
        const newSelectedPackage = bundles.find(b => b.services.some(s => s.id === scrollToServiceId));
        if (newSelectedPackage) {
          setSelectedPackage(newSelectedPackage.id);
          const serviceToScrollTo = newSelectedPackage.services.find(s => s.id === scrollToServiceId);
          serviceToScrollTo && scrollToService(serviceToScrollTo);
        }
      } else {
        const serviceToScrollTo = services.find(s => s.id === scrollToServiceId);
        serviceToScrollTo && scrollToService(serviceToScrollTo);
      }
    }
  }, [scrollToServiceId]);
  useEffect(() => {
    // We will get here if we try to send an agreement that contains a bundle without services.
    if (showEmptyWarning) {
      const {
        bundles
      } = getStateInRef();
      const emptyBundle = bundles.find(b => !b.services.length);
      emptyBundle && setSelectedPackage(emptyBundle.id);
    }
  }, [showEmptyWarning]);
  const actualServices = useMemo(() => {
    if (bundles.length) {
      const activeBundle = bundles.find(b => b.id === selectedPackage);
      return activeBundle ? activeBundle.services : services;
    }
    return services;
  }, [selectedPackage, bundles, services]);
  const onAddPackage = () => {
    const firstBundleId = generateUniqueId('bundle');
    if (!bundles.length) {
      const firstName = getFreeNewBundleName([]);
      const secondName = getFreeNewBundleName([firstName]);
      setSelectedPackage(firstBundleId);
      setBundles([{
        id: firstBundleId,
        name: firstName,
        services
      }, {
        id: generateUniqueId('bundle'),
        name: secondName,
        services: []
      }]);
      setServices([]);
    } else {
      setBundles([...bundles, {
        id: generateUniqueId('bundle'),
        name: getFreeNewBundleName(bundles.map(b => b.name)),
        services: []
      }]);
    }
  };
  const onRemovePackage = id => {
    const newBundles = bundles.filter(b => b.id !== id);
    if (newBundles.length > 1) {
      setSelectedPackage(newBundles[0].id);
      setBundles(newBundles);
    } else {
      setSelectedPackage(undefined);
      setBundles([]);
      setServices(newBundles[0].services);
    }
  };
  const onDuplicatePackage = _ref => {
    let {
      services,
      name
    } = _ref;
    const id = generateUniqueId('bundle');
    setBundles([...bundles, {
      id,
      name: "".concat(name, " Copy"),
      services: services.map(_ref2 => {
        let {
            id: _
          } = _ref2,
          service = _objectWithoutProperties(_ref2, _excluded);
        return serviceModel.makeService(service);
      })
    }]);
    setSelectedPackage(id);
  };
  const onChangePackageName = (name, bundleId) => {
    const newBundles = bundles.map(b => {
      if (b.id === bundleId) return _objectSpread(_objectSpread({}, b), {}, {
        name
      });
      return b;
    });
    setBundles(newBundles);
  };
  function handleServicesAdded(newServices) {
    if (bundles.length) {
      const newBundles = bundles.map(b => {
        if (b.id === selectedPackage) return _objectSpread(_objectSpread({}, b), {}, {
          services: [...b.services, ...newServices]
        });
        return b;
      });
      setBundles(newBundles);
    } else {
      setServices([...services, ...newServices]);
    }
    scrollToService(newServices[newServices.length - 1]);
  }
  async function onEditService(service) {
    const output = await overlayContext.showModal({
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/_jsx(ServiceBuilder, _objectSpread(_objectSpread({}, p), {}, {
        service: service,
        serviceOrigin: "draft",
        agreementData: agreementData
      }))
    });
    if (!output) return;
    applyServiceChanges(output);
  }
  async function onEditServiceNameAndDescription(service) {
    const isBundle = serviceModel.isBundle(service);
    await overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/_jsx(EditServiceContent, {
          width: "700px",
          title: "".concat(isBundle ? 'Bundle' : 'Service', " name & description"),
          serviceName: service.name,
          description: service.description,
          serviceId: service.id,
          dismissModal: dismissModal,
          onSave: _ref4 => {
            let {
              name,
              description
            } = _ref4;
            applyServiceChanges(_objectSpread(_objectSpread({}, service), {}, {
              name,
              description
            }));
            dismissModal();
          },
          bundle: service.bundle,
          showAiDescriptionPanel: true
        });
      }
    });
  }
  const applyServiceChanges = changedService => {
    const applyChangedService = services => {
      const idx = services.findIndex(s => s.id === changedService.id);
      const newServices = [...services];
      newServices[idx] = changedService;
      return newServices;
    };
    if (bundles.length) {
      const newBundles = bundles.map(b => {
        if (b.services.some(s => s.id === changedService.id)) return _objectSpread(_objectSpread({}, b), {}, {
          services: applyChangedService(b.services)
        });
        return b;
      });
      setBundles(newBundles);
    } else {
      setServices(applyChangedService(services));
    }
  };
  const onRemoveService = removedServiceId => {
    if (bundles.length) {
      const newBundles = bundles.map(b => {
        if (b.services.some(s => s.id === removedServiceId)) return _objectSpread(_objectSpread({}, b), {}, {
          services: b.services.filter(s => s.id !== removedServiceId)
        });
        return b;
      });
      setBundles(newBundles);
    } else {
      setServices(services.filter(s => s.id !== removedServiceId));
    }
  };
  const onSortServices = (changedService, intent) => {
    const newServices = moveItem(changedService, actualServices, intent);
    if (bundles.length) {
      const newBundles = bundles.map(b => {
        if (b.services.some(s => s.id === changedService.id)) return _objectSpread(_objectSpread({}, b), {}, {
          services: newServices
        });
        return b;
      });
      setBundles(newBundles);
    } else {
      setServices(newServices);
    }
  };
  function findService(serviceId) {
    if (bundles.length) {
      return concat(...bundles.map(_ref5 => {
        let {
          services
        } = _ref5;
        return services;
      })).find(s => s.id === serviceId);
    } else {
      return services.find(s => s.id === serviceId);
    }
  }
  function onDayOfMonthChange(serviceId, dayOfMonth) {
    const service = findService(serviceId);
    if (service != null) applyServiceChanges(serviceModel.setBillingDayOfMonth(service, dayOfMonth));
  }
  function onDayOfWeekChange(serviceId, dayOfWeek) {
    const service = findService(serviceId);
    if (service != null) applyServiceChanges(serviceModel.setBillingDayOfWeek(service, dayOfWeek));
  }
  function onDayOfYearChange(serviceId, dayOfYear) {
    const service = findService(serviceId);
    if (service != null) applyServiceChanges(serviceModel.setBillingDayOfYear(service, dayOfYear));
  }
  function onBillUpfrontModeChange(serviceId, billUpfront) {
    const service = findService(serviceId);
    if (service != null) applyServiceChanges(serviceModel.setBillUpfront(service, billUpfront));
  }
  function scrollToService(serviceToScrollTo) {
    const serviceEl = servicesRef.current[serviceToScrollTo.id];
    if (!serviceEl) {
      if (scrollToServiceCounter.current < 20) {
        scrollToServiceCounter.current++;
        setTimeout(() => scrollToService(serviceToScrollTo), 50);
      }
    } else {
      scrollToServiceCounter.current = 1;
      scrollIntoView(serviceEl, {
        disableScrollAnimation
      });
    }
  }
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.tabs,
      children: [!services.length && bundles.map((b, idx) => /*#__PURE__*/_jsx(ServicePackagesTab, {
        bundle: b,
        isSelected: selectedPackage === b.id,
        isReadonly: isReadOnly,
        testid: "pkg-tab-".concat(idx),
        onClick: setSelectedPackage,
        onRemove: onRemovePackage,
        onChangeName: onChangePackageName,
        onDuplicate: onDuplicatePackage,
        bundlesCount: bundles.length
      }, b.id)), bundles.length < 3 && !isReadOnly && /*#__PURE__*/_jsxs(TextButton, {
        underline: "hover",
        "data-testid": "pkg-add-btn",
        onClick: onAddPackage,
        children: ["+ ", !bundles.length ? 'Create Package' : 'Add Package']
      })]
    }), /*#__PURE__*/_jsxs("div", {
      id: tabPanelId,
      children: [/*#__PURE__*/_jsx(ServicesListStructure, {
        ref: ref,
        addServicesTestid: addServicesTestid,
        centerVertically: isInPackage,
        isReadOnly: isReadOnly,
        agreementData: agreementData,
        onClickAddServices: () => reportButtonClick(addServicesTestid),
        onServicesAdded: handleServicesAdded,
        emptyState: /*#__PURE__*/_jsxs("div", {
          className: styles.emptyStateContainer,
          children: [/*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(Typography, {
              skin: "secondary",
              as: "p",
              children: "Add the services you'd like to include in the proposal."
            }), /*#__PURE__*/_jsx(Typography, {
              skin: "secondary",
              as: "p",
              children: "Services can be customized after they've been added."
            })]
          }), /*#__PURE__*/_jsx(AddServicesButton, {
            text: "Select services",
            agreementData: agreementData,
            disabled: isReadOnly,
            testId: addServicesTestid,
            onClick: () => reportButtonClick(autoprefix + '-empty-state-btn-add-service'),
            onServicesAdded: handleServicesAdded
          }), showEmptyWarning && /*#__PURE__*/_jsxs(Typography, {
            skin: "error",
            "data-testid": autoprefix + 'empty-msg',
            children: ["You must add at least one service to", ' ', isInPackage ? 'this package' : 'your agreement']
          })]
        }),
        children: actualServices.map((service, idx) => /*#__PURE__*/_jsx(DraftServiceInstance, {
          showErrors: showErrors,
          idx: idx,
          ref: el => servicesRef.current[service.id] = el,
          service: service,
          isInvalid: isServiceInvalid(service),
          isReadOnly: isReadOnly,
          canMoveUp: 0 < idx,
          canMoveDown: idx < actualServices.length - 1,
          advancedSettingsProps: _objectSpread(_objectSpread({}, advancedSettingsProps), {}, {
            showServiceValidationWarning: showServiceValidationWarning.some(id => id === serviceModel.id(service))
          }),
          serviceDetailsProps: {
            agreementData,
            onDayOfMonthChange,
            onDayOfWeekChange,
            onDayOfYearChange,
            onBillUpfrontModeChange,
            onCostChange: cost => applyServiceChanges(serviceModel.setCost(service, cost)),
            onChangeService: service => applyServiceChanges(service),
            onIncludedServicesChange: includedServices => applyServiceChanges(serviceModel.setIncludedServices(service, includedServices))
          },
          onEditServiceNameAndDescription: onEditServiceNameAndDescription,
          onEditService: onEditService,
          onRemoveService: onRemoveService,
          onMoveDown: () => onSortServices(service, 'down'),
          onMoveUp: () => onSortServices(service, 'up')
        }, service.id))
      }), showInvalidPackagesWarning && /*#__PURE__*/_jsx(Typography, {
        className: styles.invalidWarning,
        skin: "error",
        "data-testid": "invalid-pkg-warning",
        children: "You must create at least two packages"
      })]
    })]
  });
});
export default ServicePackages;